import { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "cohesive-canvas-centric-override";

function readOverride(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEY);
}

export interface CanvasCentricContextValue {
  /** Resolved value: localStorage override if set, otherwise the feature flag. */
  isCanvasCentricApp: boolean;
  /** Raw feature flag (for showing the toggle only when flag is on). */
  canvasCentricFlag: boolean;
  setCanvasCentricApp: (value: boolean) => void;
}

const CanvasCentricContext = createContext<CanvasCentricContextValue | null>(
  null,
);

export function CanvasCentricProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const flags = useFlags<Flags>();
  const flagValue = flags.canvasCentricApp ?? false;

  const [override, setOverrideState] = useState<string | null>(readOverride);

  const isCanvasCentricApp = useMemo(() => {
    if (override !== null) {
      return override === "true";
    }
    return flagValue;
  }, [override, flagValue]);

  const setCanvasCentricApp = useCallback(
    (value: boolean) => {
      if (value === flagValue) {
        localStorage.removeItem(STORAGE_KEY);
        setOverrideState(null);
      } else {
        const str = value ? "true" : "false";
        localStorage.setItem(STORAGE_KEY, str);
        setOverrideState(str);
      }
    },
    [flagValue],
  );

  const value = useMemo<CanvasCentricContextValue>(
    () => ({
      isCanvasCentricApp,
      canvasCentricFlag: flagValue,
      setCanvasCentricApp,
    }),
    [isCanvasCentricApp, flagValue, setCanvasCentricApp],
  );

  return (
    <CanvasCentricContext.Provider value={value}>
      {children}
    </CanvasCentricContext.Provider>
  );
}

export function useIsCanvasCentricApp(): CanvasCentricContextValue {
  const ctx = useContext(CanvasCentricContext);
  if (!ctx) {
    throw new Error(
      "useIsCanvasCentricApp must be used within CanvasCentricProvider",
    );
  }
  return ctx;
}
